import { useState } from 'react';
import { sleep } from 'helpers/sleep.ts';
import { QueryFactory } from 'services/api';
import { ExportStatus } from 'services/api/api-client.types.ts';
import { useQueryClient } from '@tanstack/react-query';
import { showErrorToast } from 'components/uikit/toast/showToast';

export type UseSharePointExportProps = {
  projectId: number;
  revisionId: number;
};

export const useSharePointExport = (props: UseSharePointExportProps) => {
  const [isSharePointExporting, setIsSharePointExporting] = useState(false);
  const queryClient = useQueryClient();

  async function exportToSharePoint() {
    if (!props.revisionId || !props.projectId || isSharePointExporting) {
      return;
    }

    setIsSharePointExporting(true);
    try {
      //update folders structure before exporting
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.RevisionQuery.getQueryKey(props.revisionId),
      });

      await QueryFactory.RevisionClient.exportToSharePoint(props.revisionId);

      while (true) {
        await sleep(1000);
        const revision = await QueryFactory.RevisionClient.get(
          props.revisionId,
        );
        if (revision.sharePointExportStatus === ExportStatus.NotStarted) {
          break;
        }
      }

      //update documents and revision isExported prop after exporting
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.RevisionQuery.getQueryKey(props.revisionId),
      });
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.DocumentQuery.searchQueryKey(props.revisionId),
      });
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.ProjectQuery.getQueryKey(props.projectId),
      });
    } catch (e) {
      showErrorToast(e);
    } finally {
      setIsSharePointExporting(false);
    }
  }

  return {
    isSharePointExporting,
    exportToSharePoint,
  };
};
